<script setup lang="ts">
  import JSONEditor from 'jsoneditor'
  import { onMounted, onUnmounted, ref, watch } from 'vue'
  import { Button, message } from 'ant-design-vue'
  import { CheckCircle2 } from 'lucide-vue-next'
  import { defaultMessageDurationSeconds } from '@/utils'
  import { CustomReportSchema } from '../models/CustomReportSchema'
  import { CustomReportJsonEditorSchema } from '../services/CustomReportJsonReportConfigurationService'
  import { CustomReportsService } from '../services/CustomReportsService'
  import { CustomReportsTypeGuardService } from '../services/CustomReportsTypeGuardService'

  interface IJsonCustomReportConfigurationProps {
    customReportSchemas: Array<CustomReportSchema>
    modelValue: {}
    placeHolder?: string
    configurationLevel: number
    editorSchema: CustomReportJsonEditorSchema
    allowTypeChange: boolean
  }

  interface IJsonCustomReportConfigurationEmits {
    (e: 'update:modelValue', value: {}): void
    (e: 'customReport-type-changed', type: string, configuration: {}): void
  }

  const props = defineProps<IJsonCustomReportConfigurationProps>()
  const emit = defineEmits<IJsonCustomReportConfigurationEmits>()

  const modelValue = ref<{}>({ ...props.modelValue })

  watch(
    modelValue,
    (newVal) => {
      emit('update:modelValue', newVal)
    },
    { deep: true }
  )

  const newApplyableCustomReportType = ref<string | null>(null)

  const editorContainer = ref<HTMLElement | null>(null)
  let editor: any = null

  const initializeJSONEditor = () => {
    if (editorContainer.value) {
      editor = new JSONEditor(editorContainer.value, {
        mode: 'code',
        schema: props.editorSchema,
        onError: (err: Error) => console.error('Error:', err),
        onChange: () => {
          try {
            const newVal = editor.get()
            if (props.allowTypeChange === true) {
              onEditorChangeForCustomReport(newVal)
            } else {
              onEditorChangeForField(newVal)
            }
          } catch (err: any) {
            const editorValue = editor.getText?.() ?? ''
            if (editorValue.trim() === '') {
              message.error('Editor content cannot be empty.', defaultMessageDurationSeconds)
            }
          }
        }
      })
      setJSONEditorModel(modelValue.value)
    }
  }

  const onEditorChangeForCustomReport = (editorValue: any) => {
    newApplyableCustomReportType.value = null
    if (typeof editorValue === 'object') {
      if (editorValue.type != null) {
        const customReportType = CustomReportsService.tryGetCustomReportSchemaByType(
          editorValue.type,
          props.customReportSchemas
        )
        if (
          customReportType != null &&
          customReportType.type !== CustomReportsService.CUSTOM_CUSTOMREPORT_TYPE &&
          (props.configurationLevel === 1 ||
            !CustomReportsTypeGuardService.supportsChildCustomReports(customReportType))
        ) {
          newApplyableCustomReportType.value = customReportType.type
        }
      }
      modelValue.value = editorValue
    }
  }

  const onEditorChangeForField = (editorValue: any) => {
    modelValue.value = editorValue
  }

  const setJSONEditorModel = (value: {}) => {
    if (editor != null) {
      newApplyableCustomReportType.value = null
      editor.set(value)
      if (editor.getMode() === 'tree') editor.expandAll()
    }
  }

  const onApplyNewCustomReportType = () => {
    if (newApplyableCustomReportType != null) {
      emit('customReport-type-changed', newApplyableCustomReportType.value as string, modelValue)
    }
  }

  onMounted(() => {
    initializeJSONEditor()
  })

  onUnmounted(() => {
    if (editor) {
      editor.destroy()
    }
  })
</script>

<template>
  <div class="flex flex-col">
    <div v-if="props.allowTypeChange === true" class="mb-3 flex justify-end">
      <a-tooltip>
        <template #title>
          {{ `Apply Custom Report type: ${newApplyableCustomReportType}` }}
        </template>
        <Button type="primary" :disabled="newApplyableCustomReportType == null" @click="onApplyNewCustomReportType">
          <span class="ant-button-flex-content">
            <CheckCircle2 class="h-4 w-4" />
            {{ 'Apply Custom Report Type' }}
          </span>
        </Button>
      </a-tooltip>
    </div>
    <div ref="editorContainer" style="min-height: 500px; height: 500px"></div>
  </div>
</template>